import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Warning as WarningIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/iot'

const useStyles = makeStyles(theme => ({
  dialog: {
    minWidth: 400
  },
  warningIcon: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(1)
  },
  deviceInfo: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2)
  }
}))

export const IoTDeviceDeleteModal = ({ open, onClose, device, modalityId, onDeviceDeleted }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleDelete = async () => {
    if (!device || !modalityId) return

    setLoading(true)
    setError(null)

    try {
      await iotAPI.removeIoTDevice(modalityId, device.id)
      
      // Call the callback to refresh the device list
      if (onDeviceDeleted) {
        onDeviceDeleted()
      }
      
      // Close the modal
      onClose()
    } catch (err) {
      console.error('Error deleting IoT device:', err)
      setError(err.message || 'Failed to delete IoT device')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  if (!device) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="h6">
              Delete IoT Device
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete this IoT device? This action cannot be undone.
        </Typography>

        <Box className={classes.deviceInfo}>
          <Typography variant="subtitle2" gutterBottom>
            Device Information:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Device ID:</strong> {device.deviceId}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Device Type:</strong> {device.deviceType}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Location:</strong> {device.location || 'N/A'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Status:</strong> {device.activatedAt ? 'Active' : 'Inactive'}
          </Typography>
        </Box>

        <Alert severity="warning" style={{ marginTop: 16 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Deleting this device will:
            <br />
            • Remove all device data and configuration
            <br />
            • Invalidate any active tokens
            <br />
            • Stop data collection from this device
            <br />
            • This action cannot be undone
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="secondary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Deleting...' : 'Delete Device'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default IoTDeviceDeleteModal
